import React, { useMemo, useRef } from 'react';
import { Pie, Bar } from 'react-chartjs-2';
import { 
    Chart as ChartJS, 
    ArcElement, 
    Tooltip, 
    Legend, 
    CategoryScale, 
    LinearScale, 
    BarElement, 
    Title 
} from 'chart.js';
import { DashboardGrid, Card, CardTitle, ChartContainer } from '../styles';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const ChartsSection = ({ posts, comentarios, filtroSentimento, setFiltroSentimento, filtros }) => {
    const pieChartRef = useRef(null);

    const WEIGHTS = {
        LIKE: 1,
        SAVE: 2,
        COMMENT: 4,
        SHARE: 5
    };

    // --- CORREÇÃO 1: Lógica do Pie Chart (Sentimentos) ---
    // Removemos a lógica de 'new Date'. Se o post está na lista 'posts' (que já vem filtrada do pai), 
    // então o comentário deve aparecer.
    const comentariosFiltradosBase = useMemo(() => {
        const idsPostsVisiveis = new Set(posts.map(p => p.post_id));

        return comentarios.filter(c => {
            // 1. Verifica se o post pai está visível (Data/Pagina/Tipo)
            const postEstaVisivel = idsPostsVisiveis.has(c.post);
            if (!postEstaVisivel) return false;

            // 2. Verifica se há um filtro de post específico ativo
            const filtroPostAtual = filtros?.post; 
            const correspondePostSelecionado = filtroPostAtual === 'todos' || c.post == filtroPostAtual;

            return correspondePostSelecionado;
        });
    }, [comentarios, posts, filtros]);

    const sentimentChartData = useMemo(() => {
        const counts = { Positivo: 0, Negativo: 0, Neutro: 0 };
        comentariosFiltradosBase.forEach(c => { 
            if (c.classification === 'Positivo') counts.Positivo++;
            else if (c.classification === 'Negativo') counts.Negativo++;
            else counts.Neutro++;
        });
        return {
            labels: ['Positivos', 'Negativos', 'Neutros'],
            datasets: [{ 
                data: [counts.Positivo, counts.Negativo, counts.Neutro],
                backgroundColor: ['rgba(75, 192, 192, 0.6)', 'rgba(255, 99, 132, 0.6)', 'rgba(201, 203, 207, 0.6)'],
                borderWidth: (ctx) => {
                    const label = ctx.chart.data.labels[ctx.dataIndex];
                    const mapLabelToState = { 'Positivos': 'Positivo', 'Negativos': 'Negativo', 'Neutros': 'Neutro' };
                    return mapLabelToState[label] === filtroSentimento ? 4 : 1;
                },
                borderColor: (ctx) => {
                    const label = ctx.chart.data.labels[ctx.dataIndex];
                    const mapLabelToState = { 'Positivos': 'Positivo', 'Negativos': 'Negativo', 'Neutros': 'Neutro' };
                    return mapLabelToState[label] === filtroSentimento ? '#333' : '#fff';
                }
            }]
        };
    }, [comentariosFiltradosBase, filtroSentimento]);

    const sentimentMap = { 'Positivos': 'Positivo', 'Negativos': 'Negativo', 'Neutros': 'Neutro' };

    const pieOptions = {
        responsive: true,
        maintainAspectRatio: false,
        onClick: (event, elements, chart) => {
            if (elements.length > 0) {
                const index = elements[0].index;
                const label = chart.data.labels[index]; 
                const valorReal = sentimentMap[label];
                setFiltroSentimento(prev => prev === valorReal ? null : valorReal);
            }
        },
        plugins: {
            legend: {
                position: 'bottom',
                onClick: (e, legendItem, legend) => {
                    const label = legendItem.text;
                    const valorReal = sentimentMap[label];
                    setFiltroSentimento(prev => prev === valorReal ? null : valorReal);
                }
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const label = context.label || '';
                        const value = context.raw || 0;
                        const total = context.chart._metasets[context.datasetIndex].total;
                        const percentage = ((value / total) * 100).toFixed(1) + '%';
                        return `${label}: ${value} (${percentage})`;
                    }
                }
            }
        }
    };

    // --- CORREÇÃO 2: Lógica da Barra Ponderada (Filtros + Tooltip) ---
    const weightedEngagementData = useMemo(() => {
        let totalLikes = 0;
        let totalComments = 0;
        let totalShares = 0;
        let totalSaves = 0;

        posts.forEach(p => {
            // AQUI ESTAVA O ERRO: Precisamos checar se o usuário selecionou um post específico
            if (filtros?.post && filtros.post !== 'todos' && String(p.post_id) !== String(filtros.post)) {
                return;
            }

            totalLikes += (p.like_count || 0);
            totalComments += (p.comment_count || 0);
            totalShares += (p.share_count || 0);
            totalSaves += (p.save_count || 0);
        });

        const weightedLikes = totalLikes * WEIGHTS.LIKE;
        const weightedComments = totalComments * WEIGHTS.COMMENT;
        const weightedShares = totalShares * WEIGHTS.SHARE;
        const weightedSaves = totalSaves * WEIGHTS.SAVE;

        return {
            labels: ['Distribuição de Impacto'],
            datasets: [
                {
                    label: 'Compartilhamentos',
                    data: [weightedShares], // Valor Ponderado
                    rawCount: totalShares, // Guardamos o valor real para o tooltip
                    weight: WEIGHTS.SHARE,
                    backgroundColor: 'rgba(0, 119, 181, 0.7)',
                    barThickness: 40,
                },
                {
                    label: 'Comentários',
                    data: [weightedComments],
                    rawCount: totalComments,
                    weight: WEIGHTS.COMMENT,
                    backgroundColor: 'rgba(85, 172, 238, 0.7)',
                    barThickness: 40,
                },
                {
                    label: 'Salvos',
                    data: [weightedSaves],
                    rawCount: totalSaves,
                    weight: WEIGHTS.SAVE,
                    backgroundColor: 'rgba(195, 42, 163, 0.7)',
                    barThickness: 40,
                },
                {
                    label: 'Curtidas',
                    data: [weightedLikes],
                    rawCount: totalLikes,
                    weight: WEIGHTS.LIKE,
                    backgroundColor: 'rgba(59, 89, 152, 0.5)',
                    barThickness: 40,
                },
            ]
        };
    }, [posts, filtros]); // Adicionado 'filtros' às dependências

    const stackedBarOptions = {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: { stacked: true, grid: { display: false }, title: { display: true, text: 'Pontuação de Impacto Total' } },
            y: { stacked: true, grid: { display: false } },
        },
        plugins: {
            legend: { position: 'bottom' },
            tooltip: {
                callbacks: {
                    // --- CORREÇÃO 3: Tooltip mais claro ---
                    label: (context) => {
                        const dataset = context.dataset;
                        const label = dataset.label || '';
                        const points = context.raw || 0;
                        const count = dataset.rawCount || 0; // Pegamos o valor real que salvamos
                        const weight = dataset.weight || 1;
                        
                        let totalStack = 0;
                        context.chart.data.datasets.forEach(ds => { totalStack += ds.data[context.dataIndex]; });
                        const percentage = totalStack > 0 ? ((points / totalStack) * 100).toFixed(1) + '%' : '0%';
                        
                        // Mostra: "Comentários: 11 (44 pts) - 25%"
                        return `${label}: ${count} (${points} pts) - ${percentage} [Peso x${weight}]`;
                    }
                }
            }
        }
    };

    return (
        <DashboardGrid>
            <Card>
                <CardTitle>Composição de Impacto (Ponderado)</CardTitle>
                <p style={{fontSize: '0.85rem', color: '#666', marginBottom: '1rem'}}>
                    Mede o valor real das interações baseado no esforço do usuário.
                </p>
                <div style={{ height: '250px' }}>
                    <Bar data={weightedEngagementData} options={stackedBarOptions} />
                </div>
            </Card>

            <Card>
                <CardTitle>
                    Análise de Sentimentos 
                    {filtroSentimento && <span style={{fontSize: '0.8em', color: '#666'}}> (Filtrado: {filtroSentimento})</span>}
                </CardTitle>
                <p style={{fontSize: '0.85rem', color: '#666', marginBottom: '1rem'}}>Clique no gráfico para filtrar os comentários abaixo</p>
                <div style={{ height: '250px', position: 'relative', width: '100%' }}>
                    <Pie ref={pieChartRef} data={sentimentChartData} options={pieOptions}/>
                </div>
            </Card>
        </DashboardGrid>
    );
};

export default ChartsSection;